import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import type { PersonRef, PersonSummary } from '@familienbaum/shared';
import { useMyPerson, usePersons } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/field';
import { Pop, PopContent, PopTrigger } from '@/components/ui/menu';
import { Spinner } from '@/components/ui/misc';
import { PersonAvatar } from './person-chip';
import { cn } from '@/lib/utils';

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function toRef(person: PersonSummary): PersonRef {
  return {
    id: person.id,
    displayName: person.displayName,
    givenNames: person.givenNames,
    surname: person.surname,
    sex: person.sex,
    birthYear: person.birthYear,
    deathYear: person.deathYear,
    avatar: person.avatar,
    portrait: person.portrait,
    isUnverified: person.isUnverified,
    veiled: person.veiled,
  };
}

/**
 * Eine Zeile der Auswahlliste. `occupation` bleibt leer, wenn die Zeile nicht
 * aus der Trefferliste stammt, sondern aus der Sitzung – die eigene Person,
 * vorangestellt.
 */
interface Option {
  ref: PersonRef;
  occupation: string | null;
  isSelf: boolean;
}

/** Einzelauswahl einer Person mit Suche. */
export function PersonPicker({
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Person suchen…',
  id,
  clearable = true,
  defaultOpen = false,
  triggerClassName,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
}: {
  value: PersonRef | null;
  onChange: (person: PersonRef | null) => void;
  excludeIds?: string[];
  placeholder?: string;
  id?: string;
  clearable?: boolean;
  /** Suchfeld direkt geöffnet und fokussiert – zum sofortigen Lostippen. */
  defaultOpen?: boolean;
  /**
   * Gestalt des Auslösers überschreiben.
   *
   * Gedacht für die Verwandtschaftsbestimmung im Stammbaum: dort sind die zwei
   * Felder im Entwurf gefüllte Zeilen ohne Rahmen, weil sie nicht wie ein
   * Formular aussehen sollen, sondern wie zwei Namen, die man
   * gegenüberstellt.
   */
  triggerClassName?: string;
  /**
   * Wofür die Auswahl steht. Ohne eigene Beschriftung heißt der Knopf für
   * Vorleseprogramme nur „Max Mustermann“ – das sagt nicht, dass hier ein
   * Filter sitzt. In Formularen bleibt das Feldlabel über `id` zuständig.
   */
  'aria-label'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const me = useMyPerson();

  const { data, isFetching } = usePersons(
    useMemo(() => ({ q: debounced || undefined, pageSize: 20, sort: 'surname' }), [debounced]),
  );

  const options = useMemo<Option[]>(() => {
    const found: Option[] = (data?.items ?? [])
      .filter((person) => !excludeIds.includes(person.id))
      .map((person) => ({
        ref: toRef(person),
        occupation: person.occupation,
        isSelf: person.id === me?.id,
      }));

    if (!me || excludeIds.includes(me.id)) return found;

    const mine = found.find((option) => option.isSelf);
    const others = found.filter((option) => !option.isSelf);

    // In der Trefferliste: nach oben geholt statt irgendwo unter „E" – man
    // sucht sich selbst nicht, man erwartet sich.
    if (mine) return [mine, ...others];

    // Nicht darunter? Dann nur voranstellen, solange nichts getippt ist. Wer
    // „Müller" sucht und nicht Müller heißt, will nicht sich selbst als
    // ersten Treffer – eine Suche, die Unpassendes zeigt, ist kaputt. Ohne
    // Suchbegriff ist die Liste dagegen nur der Anfang des Bestands, und dort
    // gehört die eigene Person nach vorn.
    if (debounced) return found;
    return [{ ref: me, occupation: null, isSelf: true }, ...others];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.items, debounced, me?.id, excludeIds.join(',')]);

  return (
    <Pop open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1.5">
        <PopTrigger asChild>
          <button
            type="button"
            id={id}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedby}
            className={cn(
              'flex h-11 min-w-0 flex-1 items-center gap-2 rounded-control px-2.5 text-left sm:h-9.5',
              'border border-line bg-surface text-base text-ink shadow-inset sm:text-sm',
              'transition-[border-color,box-shadow] duration-150 hover:border-line-strong',
              'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25',
              'aria-invalid:border-danger',
              triggerClassName,
            )}
          >
            {value ? (
              <>
                <PersonAvatar person={value} size="xs" />
                <span className="min-w-0 flex-1 truncate text-base text-ink sm:text-sm">{value.displayName}</span>
                {value.birthYear ? (
                  <span className="shrink-0 text-[12px] text-ink-3 tabular">{value.birthYear}</span>
                ) : null}
              </>
            ) : (
              <span className="flex-1 truncate text-base text-ink-3 sm:text-sm">{placeholder}</span>
            )}
            <ChevronsUpDown className="size-4 shrink-0 text-ink-3" aria-hidden />
          </button>
        </PopTrigger>

        {clearable && value ? (
          <Button variant="ghost" size="iconSm" aria-label="Auswahl entfernen" onClick={() => onChange(null)}>
            <X aria-hidden />
          </Button>
        ) : null}
      </div>

      <PopContent className="p-0">
        <div className="relative border-b border-line p-2">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Name suchen…"
            className="h-11 border-0 pl-8 shadow-none focus:ring-0 sm:h-9"
          />
          {isFetching ? <Spinner className="absolute top-1/2 right-4 -translate-y-1/2" /> : null}
        </div>

        <ul className="scroll-thin max-h-64 overflow-y-auto p-1">
          {options.length === 0 ? (
            <li className="px-2 py-6 text-center text-[13px] text-ink-3">
              {debounced ? 'Nichts gefunden' : 'Noch keine Personen erfasst'}
            </li>
          ) : (
            options.map(({ ref: person, occupation, isSelf }) => {
              const selected = value?.id === person.id;
              return (
                <li key={person.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(person);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-sunken',
                      selected && 'bg-brand-soft',
                    )}
                  >
                    <PersonAvatar person={person} size="xs" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{person.displayName}</span>
                      {occupation ? (
                        <span className="block truncate text-[12px] text-ink-3">{occupation}</span>
                      ) : null}
                    </span>
                    {/* „Du" statt einer Wiederholung des Namens: die Zeile
                        steht ganz oben und sieht sonst aus wie jede andere. */}
                    {isSelf ? (
                      <span className="shrink-0 rounded-full bg-brand-soft px-1.5 py-0.5 text-[11px] text-ink-2">
                        Du
                      </span>
                    ) : null}
                    <span className="shrink-0 text-[12px] text-ink-3 tabular">
                      {person.birthYear ?? ''}
                      {person.deathYear ? `–${person.deathYear}` : ''}
                    </span>
                    {selected ? <Check className="size-4 shrink-0 text-accent-strong" aria-hidden /> : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopContent>
    </Pop>
  );
}

/** Mehrfachauswahl – für Ereignisteilnehmer und Bildmarkierungen. */
export function PersonMultiPicker({
  value,
  onChange,
  excludeIds = [],
  label = 'Personen hinzufügen',
}: {
  value: PersonRef[];
  onChange: (persons: PersonRef[]) => void;
  excludeIds?: string[];
  label?: string;
}) {
  const selectedIds = value.map((person) => person.id);

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((person) => (
            <li
              key={person.id}
              className="flex items-center gap-1.5 rounded-full border border-line bg-elevated py-0.5 pr-1 pl-1"
            >
              <PersonAvatar person={person} size="xs" />
              <span className="max-w-40 truncate text-[13px] text-ink">{person.displayName}</span>
              <button
                type="button"
                aria-label={`${person.displayName} entfernen`}
                onClick={() => onChange(value.filter((item) => item.id !== person.id))}
                className="rounded-full p-0.5 text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <PersonPicker
        value={null}
        clearable={false}
        placeholder={label}
        excludeIds={[...selectedIds, ...excludeIds]}
        onChange={(person) => {
          if (person) onChange([...value, person]);
        }}
      />
    </div>
  );
}
